import PropTypes from 'prop-types';
/**
 * Remove specific form fields from form data and update the state with the modified data.
 * 
 * @param {object} formData - The current form data object.
 * @param {Array} fieldsToRemove - An array of field dataField IDs to remove from the form data.
 */
export const removeFormFields = (formData, fieldsToRemove) => {
    // Create a copy of the formData object
    const modifiedData = { ...formData };

    // If formFields are directly in formData, filter them
    if (modifiedData.formFields) {
        modifiedData.formFields = modifiedData.formFields.filter((field) => !fieldsToRemove.includes(field.dataField));
    }

    // Normalize fieldsToRemove to lowercase for case-insensitive comparison
    const lowerCaseFieldsToRemove = fieldsToRemove.map((field) => field?.toLowerCase());

    // If there are sections, iterate over each to filter fields and nested fields
    if (modifiedData.section) {
        modifiedData.section = modifiedData.section.map((section) => {
            // Remove fields at the section level
            const updatedSection = { ...section };
            if (updatedSection.fields) {
                updatedSection.fields = updatedSection.fields.filter((field) => !lowerCaseFieldsToRemove.includes(field.dataField?.toLowerCase()));
            }

            // If there are rowGroups, filter fields within each row
            if (updatedSection.rowGroup) {
                updatedSection.rowGroup = updatedSection.rowGroup.map((row) => {
                    const updatedRow = { ...row };
                    updatedRow.fields = updatedRow.fields.filter(
                        (field) => !fieldsToRemove.includes(field.id)
                    );
                    return updatedRow;
                });
            }

            return updatedSection;
        });
    }

    // Return the modified form data
    return modifiedData;

};
// Define propTypes for the function parameters
removeFormFields.propTypes = {
    formData: PropTypes.object.isRequired,
    fieldsToRemove: PropTypes.array.isRequired
};

/**
 * Remove specific form fields from form data and update the state with the modified data.
 * 
 * @param {object} formFieldConfiguration - The current form data object.
 * @param {Array} fieldsToRemove - An array of field dataField IDs to remove from the form data.
 */
export const addFormFields = (formFieldConfiguration, fieldsToAdd) => {

    // Create a copy of the formFieldConfiguration object
    const modifiedData = { ...formFieldConfiguration };

    // Helper function to add the field to a section or rowGroup
    const addFieldToSectionOrGroup = (section, field) => {
        if (section.fields) {
            section.fields.push(field); // Add field to section's fields
        } else {
            section.fields = [field]; // Create a new fields array if not present
        }

        // Check if rowGroups exist and add the field to them as well
        if (section.rowGroup) {
            section.rowGroup.forEach((group) => {
                if (group.fields) {
                    group.fields.push(field); // Add field to row group's fields
                } else {
                    group.fields = [field]; // Create a new fields array if not present
                }
            });
        }
    };

    fieldsToAdd.forEach((fieldId) => {
        // Find the field in the existing configuration
        const field = findFieldsById(modifiedData, fieldId);

        if (field) {
            // Add the field to all relevant sections/rowGroups
            modifiedData.section.forEach((section) => {
                addFieldToSectionOrGroup(section, field);
            });
        }
    });

    // Update the state with the modified form data
    return modifiedData;
};


export const onResetForm = (formData, setFormData, initialState = null, otherSettersWithValues = []) => {
    // Create a copy of the form data and reset it to its initial state
    let resetData = { ...formData };
    const initialData = !initialState ? resetData.initialState : initialState;
    resetData.initialState = { ...initialData };
    setFormData && setFormData(resetData);

    // Reset other states with specified values if provided
    if (otherSettersWithValues.length > 0) {
        otherSettersWithValues.forEach(({ setter, value }) => setter(value));
    }
};
// Define propTypes for the function parameters
addFormFields.propTypes = {
    formFieldConfiguration: PropTypes.object.isRequired,
    fieldsToAdd: PropTypes.array.isRequired
};


/**
 * Finds a field by its ID from a given form field configuration.
 * @param {Object} formFieldConfiguration - The configuration object containing form data.
 * @param {string} fieldId - The ID of the field to search for.
 * @returns {Object|null} - The found field or null if not found.
 */
export const findFieldsById = (formFieldConfiguration, fieldId) => {
    if (!formFieldConfiguration?.section) {
        return null; // Return null if the configuration or sections are invalid.
    }

    for (const section of formFieldConfiguration.section) {
        // Check if field is directly in section.fields
        const field = section.fields?.find(field => field.id === fieldId);
        if (field) return field;

        // Check if field is in any of the rowGroups
        const groupField = section.rowGroup?.flatMap(group => group.fields).find(field => field.id === fieldId);
        if (groupField) return groupField;
    }

    return null; // Return null if the field is not found.
}



/**
 * Sets a specific field setting for a given field within form fields data.
 * 
 * @param {object} formFieldsData - The object containing form fields data.
 * @param {string} fieldId - The identifier for the field to be updated.
 * @param {string} fieldSettingType - The type of setting to be updated (e.g., 'isMultiSelect', 'isDisabled') and Also, create Enums for the setting type.
 * @param {any} [value=false] - The value to set for the specified setting type. Default is `false`.
 * 
 * Example usage:
 * setFieldSetting(formFieldsData, 'countryDropdown', FieldSettingType.DISABLED/FieldSettingType.MULTISELECT, true);
 */
export const setFieldSetting = (formFieldsData, fieldId, fieldSettingType, value = false) => {
    const selectField = findFieldsById(formFieldsData, fieldId);
    if (selectField?.fieldSetting) {
        switch (fieldSettingType) {
            case 'isMultiSelect':
                selectField.fieldSetting.isMultiSelect = value;
                break;
            case 'isDisable':
                selectField.fieldSetting.isDisable = value;
                break;
            case 'isPrimaryButtonVisible':
                selectField.inputButtonGroupConfig.isPrimaryButtonVisible = value;
                break;
            case 'isInfoButtonVisible':
                selectField.fieldSetting.isFormControl = value;
                break;
            case 'isText':
                selectField.fieldSetting.isText = value;
                break;
            case 'CKEditorDisabled':
                selectField.fieldSetting.isDisable = value;
                break;
            case 'isMultiple':
                selectField.fieldSetting.isMultiple = value;
                break;
            case 'isFileNameCleared':
                selectField.fieldSetting.isFileNameCleared = value;
                break;
            case 'isTemplateBtnDisable':
                selectField.fieldSetting.isTemplateBtnDisable = value;
                break;
            default:
                break;
        }
    }
};

export const setDropDownOptionField = (apiResponseData, valueField, labelField, formFieldsData, fieldId, filterCondition = null, setFormData, extraKeys = []) => {

    // Filter the API response data if a filter condition is provided
    const filteredData = filterCondition ? apiResponseData?.filter(filterCondition) : apiResponseData;

    // Map the filtered data to the required format
    const mappedData = filteredData?.map((item) => {
        const options = {
            value: item[valueField],
            label: item[labelField],
        }

        // Dynamically add additional properties from extraKeys
        extraKeys?.forEach(key => {
            if (key in item) {
                options[key] = item[key];
            }
        });

        return options;
    });

    // Find the dropdown field within the form fields data
    const dropdownField = getFieldData(formFieldsData, fieldId);

    // If the dropdown field is found, set its options to the mapped data
    if (dropdownField) {
        dropdownField.fieldSetting.options = mappedData;
    }
    setFormData && setFormData({ ...formFieldsData });
};


export const getFieldData = (formFieldsData, fieldId) => {
    if (formFieldsData?.section) {
        for (const section of formFieldsData?.section) {
            let field
            if (section.fields) {
                field = section.fields.find((item) => item.dataField === fieldId);
                if (field) return field;
            }

            if (section.rowGroup) {
                for (const row of section.rowGroup) {
                    field = row.fields.find((field) => field.id === fieldId);
                    if (field) return field;
                }
            }
        }
    }
    return null;
};
// Define propTypes for the function parameters
setFieldSetting.propTypes = {
    formFieldsData: PropTypes.array.isRequired,
    fieldId: PropTypes.string.isRequired,
    fieldSettingType: PropTypes.array.isRequired,
    value: PropTypes.bool
};